import { useState } from 'react';
import { Popover } from '~/components/ui/popover';
import { useActiveChats } from '~/hooks/queries/use-chats';
// FRONT-3223: this menu is the `eddy.ui-refactoring` OFF path of the chat window,
// so it renders the pre-FRONT-3144 board and cluster (the ones with the
// footer "New chat" button, the header pills and the bordered trigger).
import { ActivityBoardLegacy as ActivityBoard } from '~/features/chat/top-bar/legacy/activity-board-legacy';
import { StatusClusterLegacy as StatusCluster } from '~/features/chat/top-bar/legacy/status-cluster-legacy';
import { useChatActivity } from '~/features/chat/top-bar/use-chat-activity';
import { chatThreadToAsset, type ModalAssetItem } from '~/features/chat/modal-asset';

/**
 * The zen modal's "Chat activity" dropdown (FRONT-1579) — replaces the old
 * SWITCH pill strip (`chat-switch-bar.tsx`, FRONT-518/FRONT-1337) with the
 * exact same status-cluster-trigger + activity-board popover the app top bar
 * shows, so hopping between open threads is one control the user already
 * knows, not a second bespoke one living only inside the modal.
 *
 * Mounted in the modal's eyebrow header row (not a full-width strip below
 * it, like the old bar). Reuses `useChatActivity` for the board's
 * groups/counts — the same hook `ChatTopBar` reads, so the two boards can't
 * disagree — and calls `useActiveChats()` directly for the raw thread rows a
 * row click needs to resolve into a modal asset via `chatThreadToAsset`
 * (same query key `useChatActivity` reads internally, so this costs no
 * extra request, only a second cached subscription — the same pattern
 * `use-chat-pills.ts` already documents for its two callers).
 *
 * The currently-open thread's row is marked (`currentSessionId`) so it
 * doesn't look identical to every other row and read as a dead control —
 * clicking it is the no-op guard below, not a switch.
 */
export function ChatActivityMenu({
  openSessionId,
  onSwitch,
  onNewChat,
}: {
  /** Session id of the chat currently open — clicking its own row is a no-op. */
  openSessionId: string;
  /** Swap the modal onto another thread. The caller remounts on the new asset. */
  onSwitch: (item: ModalAssetItem) => void;
  /**
   * Starts a fresh chat — the modal's own "New chat" control, reused rather
   * than inventing a second new-chat path. Optional: not every
   * `InboxChatModal` mount site has a handler wired, and one with none has
   * no sensible "start a fresh chat in place" action to offer — the footer
   * button hides rather than render as a no-op. FRONT-3223: this menu is the
   * `eddy.ui-refactoring` OFF path; the reworked `ActivityBoard` dropped its
   * `onNew` footer, so the handler reaches it only once the legacy board
   * carries that prop again (see `boardExtras`).
   */
  onNewChat?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { groups, counts } = useChatActivity();
  const { data: threads = [] } = useActiveChats();

  function openFromBoard(sessionId: string) {
    setOpen(false);
    // Clicking the chat you are already in is a no-op, not a switch — the
    // same guard `chat-switch-bar.tsx` had (FRONT-1337): treating it as a
    // switch would strip the one-shot open props (switch-open-props.ts)
    // without a remount, which on a Work-scoped chat makes the "About:
    // <project>" chip blink out and refetch.
    if (sessionId === openSessionId) return;
    const row = threads.find((t) => t.sessionId === sessionId);
    if (row) onSwitch(chatThreadToAsset(row));
  }

  function newFromBoard() {
    setOpen(false);
    onNewChat?.();
  }

  // Empty without a handler, so the board's footer button hides (its own `onNew?`).
  const boardExtras: Partial<Record<'onNew', () => void>> = onNewChat ? { onNew: newFromBoard } : {};

  return (
    // `top-bar.css`'s scope: every root the top-bar components render into
    // carries `.eddy-topbar` (the header's own left group does, via
    // `chat-top-bar.tsx`; `ActivityBoard` carries its own copy since it
    // portals outside this subtree). This trigger's the one root that would
    // otherwise render without it.
    <span className="eddy-topbar inline-flex">
      <Popover open={open} onOpenChange={setOpen}>
        <StatusCluster counts={counts} size="sm" />
        <ActivityBoard
          groups={groups}
          onOpen={openFromBoard}
          currentSessionId={openSessionId}
          {...boardExtras}
        />
      </Popover>
    </span>
  );
}
